import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deletePage,
  getPagesList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  PageItem,
  updatePageBunch,
} from "store/cms/pages/list/slice";
import { cmsPagesListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import PagesFilter from "./components/PagesFilter";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import EmptyState from "components/EmptyState/EmptyState";
import { Table } from "components/table/Table";

import { NotificationTypes, showNotification } from "store/notifications/slice";
import { GuideButton } from "modals/guides/GuideButton";
import { useGuideModal } from "modals/guides/useGuideModal";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const List: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { openGuide } = useGuideModal();

  const [currentLimit, setLimit] = useState<number>(cmsPagesListLimits[0]);
  const [order, setOrder] = useState({
    title: "none",
    id: "none",
    isActive: "none",
  });

  const [filters, setFilters] = useState<{
    search: string;
    active?: number;
  }>({ search: "" });

  const list = useAppSelector((s) => s["cms-pages"].list);
  const isListFetched = useAppSelector((s) => s["cms-pages"].isListFetched);
  const totalItems = useAppSelector((s) => s["cms-pages"].totalItems);
  const totalPages = useAppSelector((s) => s["cms-pages"].totalPages);
  const activePage = useAppSelector((s) => s["cms-pages"].activePage);
  const selectedItems = useAppSelector((s) => s["cms-pages"].selectedItems);
  const processingItemId = useAppSelector(
    (s) => s["cms-pages"].prosessingItemId,
  );

  const fetchList = () => {
    dispatch(
      getPagesList({
        limit: currentLimit,
        offset: activePage - 1,
        title: filters.search,
        isActive: filters.active,
        order,
      }),
    );
  };

  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cms-pages',
    isListFetched
  });

  useEffect(() => {
    if (!isListFetched) {
      fetchList();
    }
  }, [activePage, currentLimit, isListFetched]);

  const handleApplyFilter = (params: { search: string; active?: number }) => {
    setFilters(params);
    dispatch(handlePaginate(1));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    const next =
      current === "none" ? "asc" : current === "asc" ? "desc" : "none";

    setOrder({
      title: "none",
      id: "none",
      isActive: "none",
      [key]: next,
    });

    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    const result = await dispatch(deletePage(id ?? 0));

    dispatch(
      showNotification({
        title: result?.payload ? t("success") : t("error"),
        message: result?.payload
          ? t("entityDeleteSuccess")
          : t("entityDeleteError"),
        type: result?.payload
          ? NotificationTypes.success
          : NotificationTypes.error,
      }),
    );
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.page}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.page }));
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <PagesFilter onApply={handleApplyFilter} />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addPage")}
                handleClick={() => {
                  navigate(`${location.pathname}?activeModal=${ModalTypes.page}`);
                  dispatch(openModal({ activeModal: ModalTypes.page }));
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box page-list-header--box-actions">
            <GuideButton onClick={() => openGuide("cms")} />
            <DefaultDropdown
              placeholder={t("chooseAction")}
              options={[
                { label: t("activate"), value: "activate" },
                { label: t("hide"), value: "hide" },
              ]}
              isSmall
              disabled={selectedItems.length === 0 || !permissions?.update}
              onSelect={(e) =>
                dispatch(
                  updatePageBunch({
                    ids: selectedItems,
                    isActive: e === "activate" ? 1 : 0,
                  }),
                )
              }
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={6} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {list.length === 0 ? (
              <EmptyState message={t("noPageFound")} />
            ) : (
              <Table
                data={list as Array<PageItem & { id: number }>}
                order={order}
                idField="id"
                processingId={processingItemId}
                selectedItems={selectedItems}
                onAllSelect={() => dispatch(handleSelectAll())}
                onSelect={(id) => dispatch(handleSelectItem(id))}
                onDelete={handleDeleteItem}
                onSort={(id) => id && handleOrderClick(id)}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
              />
            )}
          </div>
        )}
        {isListFetched && list.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={cmsPagesListLimits}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={() =>
              activePage < totalPages &&
              dispatch(handlePaginate(activePage + 1))
            }
            handlePrevClick={() =>
              activePage > 1 && dispatch(handlePaginate(activePage - 1))
            }
          />
        )}
      </div>
    </div>
  );
};

export default List;
